import React, { useEffect, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useApiUrl } from './ApiContext';

function AgentFind() {
  const apiUrl = useApiUrl();
  const [filters, setFilters] = useState({
    locale: '',
    speciality: '',
    searchQuery: '',
  });
  const [agents, setAgents] = useState([]);

  // Fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await axios.get(`${apiUrl}/agents`);
        const formatted = res.data.map(agent => ({
          ...agent,
          speciality: agent.speciality || null,
          coords: agent.coords || (agent.Latitude && agent.Longitude ? [agent.Latitude, agent.Longitude] : null)
        }));
        setAgents(formatted);
      } catch (err) {
        console.error("Error fetching agents:", err);
      }
    };
    fetchAgents();
  }, [apiUrl]);

  const locales = [...new Set(agents.map(agent => agent.locale).filter(Boolean))].sort();

  const specialityOptions = [
    "All Specialties",
    "All Crops",
    "Game Cover Only",
    "None"
  ];

  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,
      shadowUrl: markerShadow,
    });

    const map = L.map('map', {
      center: [54.5, -2.5],
      zoom: 6,
      minZoom: 5,
      maxZoom: 15,
      maxBounds: [
        [49.5, -10],
        [59, 3],
      ],
      maxBoundsViscosity: 1.0,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    let markers = [];

    const updateMarkers = () => {
      markers.forEach(marker => map.removeLayer(marker));
      markers = [];

      agents
        .filter(agent => {
          // SPECIALTY FILTER
          if (filters.speciality && filters.speciality !== "All Specialties") {
            if (filters.speciality === "None") {
              if (agent.speciality !== null) return false;
            } else {
              if ((agent.speciality || "").toLowerCase() !== filters.speciality.toLowerCase()) return false;
            }
          }

          // LOCALE FILTER
          if (filters.locale && agent.locale !== filters.locale) return false;

          // SEARCH FILTER
          if (filters.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            const matches = Object.values(agent)
              .filter(v => v !== null && v !== undefined)
              .some(value => value.toString().toLowerCase().includes(searchLower));
            if (!matches) return false;
          }

          return true;
        })
        .forEach(agent => {
          if (!agent.coords) return;

          const popupFields = [
            `<b>${agent.name}</b>`,
            agent.locale ? `<b>Locale:</b> ${agent.locale}` : '',
            `<b>Specialty:</b> ${agent.speciality || 'None'}`,
            agent.telephone ? `<b>Telephone:</b> ${agent.telephone}` : '',
            agent.email ? `<b>Email:</b> <a href="mailto:${agent.email}">${agent.email}</a>` : ''
          ].filter(Boolean).join('<br>');

          const marker = L.marker(agent.coords).addTo(map);
          marker.bindPopup(popupFields);
          markers.push(marker);
        });
    };

    updateMarkers();

    return () => {
      map.remove();
    };
  }, [filters, agents]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearSearch = () => {
    setFilters(prev => ({ ...prev, searchQuery: '' }));
  };

  return (
    <div className="agentfind-container">
      <h1>Find Your Agent</h1>
      <div className="agentfind-filters">
        <div className="agentsearch-bar-container">
          <input
            className="agentsearch-bar"
            type="text"
            name="searchQuery"
            value={filters.searchQuery}
            onChange={handleFilterChange}
            placeholder="Search using keywords"
          />
          {filters.searchQuery && (
            <button className="clear-search-button" onClick={clearSearch}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        <div className="agentfind-selects">
          <select name="locale" value={filters.locale} onChange={handleFilterChange}>
            <option value="">National</option>
            {locales.map(locale => (
              <option key={locale} value={locale}>{locale}</option>
            ))}
          </select>
          <select name="speciality" value={filters.speciality} onChange={handleFilterChange}>
            {specialityOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>
      <div id="map"></div>
    </div>
  );
}

export default AgentFind;
